import { useEffect, useMemo, useRef, useState } from 'react'
import { ChevronLeft, ChevronRight, Printer, Tag } from 'lucide-react'
import { apiGet, cachedGet, patchJson } from '../lib/api.js'
import ApptLabFlag from './lab/ApptLabFlag.jsx'
import { buildApptLabIndex, bkkDayWindow, apptLabSummary } from '../lib/lab.js'
import { buildDocInfo, shiftEligible, doctorEligible } from '../lib/doctorFilter.js'
import { isBookableType, buildTreatmentOptions, currentTreatmentLabel, CLEAR_LABEL } from '../lib/apptTypes.js'
import { nameInitial } from '../lib/text.js'
import { useXanoRealtime } from '../lib/realtime.js'
import { useMe } from '../lib/me.js'
import { statusTone, STATUS_TONES } from '../data/patientStatuses.js'
import { r48State, STATUS_TH, codeCls, R48_EXPIRED_CLS } from '../data/appointmentStatuses.js'
import { scaleStatus } from '../lib/scale.js'
import ApptStatusLegend from './ApptStatusLegend.jsx'
import ScaleTag from './ScaleTag.jsx'
import PlanChips from './huddle/PlanChips.jsx'
import { useHuddleStaff, assigneeNames } from '../lib/useHuddleStaff.js'
import { planKey, planOutcome, groupSold } from '../lib/huddle.js'
import UpsellTags, { upsellTags, UPSELL_MARK } from './UpsellTags.jsx'
import {
  Button, IconButton, Segmented, ViewToggle, SearchSelect, BranchSelect, Switch, FilterCard,
  DataTable, PatientLink, useToast, ConfirmDialog,
  Loading, EmptyState, NoMatchState, ErrorState,
  fmtMonth, fmtDateText,
} from '../kit'

/**
 * Branch scheduling calendar. Two steps:
 *   1. MONTH view (default) — each day lists the doctors on shift (timetable),
 *      with their shift start time. Click a day →
 *   2. DAY view — a column per eligible doctor showing their working window,
 *      blocks, and booked appointments; click a free area to book.
 *
 * Filter doctrines (apply to both): ท่านเดิม / ทันตแพทย์เดิม (patient's previous doctors) and
 * ตามการรักษา (treatment → doctor.spec / doctor.except, with per-shift
 * custom_spec/custom_except overrides). Branch selectable. Times Asia/Bangkok.
 */
const BKK = 7 * 3600 * 1000
const pad = (n) => String(n).padStart(2, '0')
const asList = (r) => (Array.isArray(r) ? r : (r?.items || []))
// Full weekday for the day header (the kit only has the short form — kit gap).
const TH_DAYS_FULL = ['อาทิตย์', 'จันทร์', 'อังคาร', 'พุธ', 'พฤหัสบดี', 'ศุกร์', 'เสาร์']
const WEEK_HEAD = ['จันทร์', 'อังคาร', 'พุธ', 'พฤหัส', 'ศุกร์', 'เสาร์', 'อาทิตย์'] // Monday-first

const DAY_START = 9 * 60, DAY_END = 21 * 60, SNAP = 15, PXM = 22 / 15 // 15 min = 22px (roomier for drag)
// How long after its OWN write this calendar ignores a realtime event carrying
// its own employee_id — long enough to cover the write → trigger → socket round
// trip (typically < 500 ms), short enough that the next own-looking event from
// another tab still lands. See the realtime handler.
const OWN_WRITE_GRACE_MS = 2500
// Status labels/badge colours: src/data/appointmentStatuses.js. The badge shows
// the raw code (per request) and the Thai label rides along as the tooltip.

const todayKey = () => { const d = new Date(Date.now() + BKK); return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}` }
const bkkMin = (ms) => { const d = new Date(ms + BKK); return d.getUTCHours() * 60 + d.getUTCMinutes() }
const hm = (min) => `${pad(Math.floor(min / 60))}:${pad(min % 60)}`
// absolute ms for a Bangkok-day ('YYYY-MM-DD') + minutes-from-midnight
const dayMinToMs = (dk, min) => { const [y, mo, d] = dk.split('-').map(Number); return Date.UTC(y, mo - 1, d, Math.floor(min / 60), min % 60) - BKK }

// Lay overlapping appointments into side-by-side lanes so all stay visible.
// Each returned appt gets {lane, cols}: width = 1/cols, left = lane/cols.
function layoutAppts(appts) {
  const evs = appts.map((a) => ({ ...a })).sort((x, y) => x.s - y.s || x.e - y.e)
  const laneEnds = []
  for (const ev of evs) {
    let lane = laneEnds.findIndex((end) => end <= ev.s)
    if (lane === -1) { lane = laneEnds.length; laneEnds.push(ev.e) } else laneEnds[lane] = ev.e
    ev.lane = lane
  }
  let cluster = [], clusterEnd = -1
  const flush = () => { const cols = cluster.length ? Math.max(...cluster.map((e) => e.lane)) + 1 : 1; cluster.forEach((e) => { e.cols = cols }); cluster = [] }
  for (const ev of evs) {
    if (cluster.length && ev.s >= clusterEnd) { flush(); clusterEnd = -1 }
    cluster.push(ev)
    clusterEnd = clusterEnd === -1 ? ev.e : Math.max(clusterEnd, ev.e)
  }
  flush()
  return evs
}

// ---- remembered view state -------------------------------------------------
// Leaving the appointments page and coming back should land where the user was
// — same view, branch, doctor, filters — instead of resetting to month/today/all.
// Only the pages that own a persistent schedule pass `persistKey`; the patient
// booking flow deliberately starts clean every time.
//
// DATE keys are restored only when the state was saved on the SAME Bangkok day:
// re-opening the page tomorrow morning should show today, not whatever day was
// last being browsed. Everything else survives across days.
const VIEW_STATE_V = 1
const viewStateKey = (k) => `ss.cal.${k}`

function loadViewState(persistKey) {
  if (!persistKey) return {}
  try {
    const raw = JSON.parse(localStorage.getItem(viewStateKey(persistKey)) || 'null')
    if (!raw || raw.v !== VIEW_STATE_V) return {}
    const { v, savedDay, ...rest } = raw
    if (savedDay !== todayKey()) { delete rest.day; delete rest.monthAnchor }
    return rest
  } catch { return {} }
}

function saveViewState(persistKey, state) {
  if (!persistKey) return
  try {
    localStorage.setItem(viewStateKey(persistKey), JSON.stringify({ v: VIEW_STATE_V, savedDay: todayKey(), ...state }))
  } catch { /* private mode / quota — remembering the view is not worth throwing over */ }
}

export default function BookingCalendar({ branchId, prevDoctors = [], allowPrevDoctors = true, liveUpdates = false, refreshKey = 0, persistKey = '', onPickSlot, onEditAppt, onScopeChange }) {
  const me = useMe()
  const [saved] = useState(() => loadViewState(persistKey))   // read once, on mount
  const [liveTick, setLiveTick] = useState(0) // bump on a realtime event → refetch the active view in place
  const [calView, setCalView] = useState(() => saved.calView || 'month') // 'month' | 'day'
  const [monthAnchor, setMonthAnchor] = useState(() => {
    const m = /^(\d{4})-(\d{2})$/.exec(saved.monthAnchor || '')
    if (m) return new Date(Number(m[1]), Number(m[2]) - 1, 1)
    const d = new Date(Date.now() + BKK); return new Date(d.getUTCFullYear(), d.getUTCMonth(), 1)
  })
  const [day, setDay] = useState(() => saved.day || todayKey())

  const [monthRows, setMonthRows] = useState([])
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(false)

  const [docInfo, setDocInfo] = useState({})
  const [types, setTypes] = useState({ list: [], byType: {}, byId: {}, flat: [] })
  const [branches, setBranches] = useState([])
  const [branchSel, setBranchSel] = useState(() => saved.branchSel || '')
  const [mode, setMode] = useState(() => saved.mode || 'all')
  const [treatType, setTreatType] = useState(() => saved.treatType || '')
  const [treatId, setTreatId] = useState(() => saved.treatId || '')
  const [pickedDoc, setPickedDoc] = useState(() => saved.pickedDoc || '') // single doctor focus ('' = all)
  const [dayView, setDayView] = useState(() => saved.dayView || 'calendar') // day step: 'calendar' | 'table' | 'card'
  const [tick, setTick] = useState(0) // bump to refetch the day in place (after drag/resize)
  const [confirmedOnly, setConfirmedOnly] = useState(() => !!saved.confirmedOnly) // day view: show only C / C*
  const [hideTreated, setHideTreated] = useState(() => !!saved.hideTreated)       // day view: hide appts that already had a treatment
  const toast = useToast()   // confirmation after drag/resize (kit toast)
  // A failed schedule load used to be swallowed (rows → [] looked like "no
  // doctors today"). Now it says so: loadError for the month/day fetch,
  // setupError for the doctors/types/branches lists the whole calendar needs.
  const [loadError, setLoadError] = useState(null)
  const [setupError, setSetupError] = useState(null)
  const [setupKey, setSetupKey] = useState(0) // bump → retry the setup lists

  // อารีย์ branch id — the fallback default when the patient's home branch is สนามเป้า (#1).
  const areeId = useMemo(() => branches.find((b) => b.name.includes('อารีย์'))?.id || '', [branches])
  // Default to the patient's branch; สนามเป้า(#1) is hidden → fall back to อารีย์.
  // Only follow the branch PROP when the caller supplies one (patient booking).
  // On the standalone appointments page it is undefined, and forcing '' here
  // would wipe the branch restored from the saved view.
  useEffect(() => {
    if (branchId == null || branchId === '') return
    setBranchSel(String(branchId) !== '1' ? String(branchId) : '')
  }, [branchId])
  useEffect(() => { if (areeId && (branchSel === '' || branchSel === '1')) setBranchSel(areeId) }, [areeId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Doctors (spec/except) + types + branches — once.
  useEffect(() => {
    let alive = true
    Promise.allSettled([cachedGet('/api/doctors'), cachedGet('/api/appointment-types'), cachedGet('/api/branches')]).then(([dr, ty, br]) => {
      if (!alive) return
      const failed = [dr.status === 'rejected' && 'ทันตแพทย์', ty.status === 'rejected' && 'ประเภทการรักษา', br.status === 'rejected' && 'สาขา'].filter(Boolean)
      setSetupError(failed.length ? `โหลดรายชื่อ${failed.join(' / ')}ไม่สำเร็จ` : null)
      setDocInfo(buildDocInfo(dr.status === 'fulfilled' ? asList(dr.value) : []))
      const list = [], byType = {}, byId = {}
      for (const t of (ty.status === 'fulfilled' ? asList(ty.value) : [])) {
        const tid = String(t.id); const type = (t.type || '').trim(); const detail = (t.detail || '').trim()
        if (!tid) continue
        // byId stays COMPLETE (it labels existing appointments); only the picker
        // lists skip retired rows — see lib/apptTypes.js.
        byId[tid] = { type, detail, duration: Number(t.duration) || 0 }
        if (type && isBookableType(t)) { if (!byType[type]) { byType[type] = []; list.push(type) } byType[type].push({ id: tid, detail: detail || type }) }
      }
      // ONE searchable list for the การรักษา picker — see lib/apptTypes.js.
      setTypes({ list, byType, byId, flat: buildTreatmentOptions(list, byType) })
      const bl = (br.status === 'fulfilled' ? asList(br.value) : [])
        .map((b) => ({ id: String(b.id ?? b.branch_id ?? ''), name: b.name || b.branch_name || `#${b.id}` }))
        .filter((b) => b.id && b.id !== '1')                 // hide สนามเป้า (#1)
        .sort((x, y) => Number(x.id) - Number(y.id))         // sort by branch_id
      setBranches(bl)
    })
    return () => { alive = false }
  }, [setupKey])

  // Morning-huddle upsell plans overlaid on the schedule, so whoever greets the
  // patient sees what the team decided to pitch. One request per view; joined to
  // rows by appointment id.
  const [plans, setPlans] = useState([])
  const [sold, setSold] = useState([])
  // Day view only — which of today's visits have lab work, and whether it is in.
  // null while unknown, so ApptLabFlag draws nothing rather than "no lab".
  const [labIndex, setLabIndex] = useState(null)
  const [promoNames, setPromoNames] = useState(new Map())

  useEffect(() => {
    cachedGet('/api/promotions')
      .then((r) => setPromoNames(new Map(asList(r).map((x) => [Number(x.id), x.Name || `#${x.id}`]))))
      .catch(() => {})
  }, [])

  useEffect(() => {
    onScopeChange?.({ branchId: branchSel, day, calView })
  }, [onScopeChange, branchSel, day, calView])

  useEffect(() => {
    saveViewState(persistKey, {
      calView,
      day,
      monthAnchor: `${monthAnchor.getFullYear()}-${pad(monthAnchor.getMonth() + 1)}`,
      branchSel, mode, treatType, treatId, pickedDoc, dayView, confirmedOnly, hideTreated,
    })
  }, [persistKey, calView, day, monthAnchor, branchSel, mode, treatType, treatId, pickedDoc, dayView, confirmedOnly, hideTreated])

  // MONTH: timetable (doctor shifts) for the whole month.
  useEffect(() => {
    if (calView !== 'month' || !branchSel) { return }
    const y = monthAnchor.getFullYear(), m = monthAnchor.getMonth()
    const start = `${y}-${pad(m + 1)}-01`
    const end = `${y}-${pad(m + 1)}-${pad(new Date(y, m + 1, 0).getDate())}`
    let alive = true
    setLoading(true)
    setLoadError(null)
    apiGet('/api/appointments', { type: 'timetable', branch_id: branchSel, start_date: start, end_date: end })
      .then((r) => { if (alive) { setMonthRows(asList(r)); setLoading(false) } })
      .catch((e) => { if (alive) { setMonthRows([]); setLoading(false); setLoadError(e?.message || 'โหลดตารางทันตแพทย์ไม่สำเร็จ') } })
    apiGet('/api/huddle-upsell', { start_date: start, end_date: end, branch_id: branchSel })
      .then((r) => { if (alive) { setPlans(asList(r?.plans)); setSold(asList(r?.sold)) } })
      .catch(() => { if (alive) { setPlans([]); setSold([]) } })
    return () => { alive = false }
  }, [calView, branchSel, monthAnchor, refreshKey, liveTick])

  // DAY: dashboard (availability + blocks + appts) for the selected day.
  useEffect(() => {
    if (calView !== 'day' || !branchSel || !day) { return }
    let alive = true
    setLoading(true)
    setLoadError(null)
    apiGet('/api/appointments', { type: 'dashboard', branch_id: branchSel, start_date: day, end_date: day })
      .then((r) => { if (alive) { setRows(asList(r)); setLoading(false) } })
      .catch((e) => { if (alive) { setRows([]); setLoading(false); setLoadError(e?.message || 'โหลดตารางนัดไม่สำเร็จ') } })
    apiGet('/api/huddle-upsell', { plan_date: day, branch_id: branchSel })
      .then((r) => { if (alive) { setPlans(asList(r?.plans)); setSold(asList(r?.sold)) } })
      .catch(() => { if (alive) { setPlans([]); setSold([]) } })
    // The day's lab work, for the flask on each block. Windowed on `lab.date`, which
    // FOLLOWS the linked appointment — so "labs dated today" is very nearly "labs for
    // today's visits", and lib/lab.js#labsForAppt does the last mile (explicit
    // appointment_id first, same-patient-same-day second). NOT branch-filtered: the
    // branch on a lab comes from its treatment join and can differ from the branch the
    // visit is booked at, and a missing flask is worse than an extra one.
    const win = bkkDayWindow(day)
    if (win) {
      apiGet('/api/lab/by-date', { from_ms: win.from, to_ms: win.to, per_page: 200 })
        .then((r) => { if (alive) setLabIndex(buildApptLabIndex(asList(r))) })
        .catch(() => { if (alive) setLabIndex(null) })  // no flag beats a wrong flag
    }
    return () => { alive = false }
  }, [calView, branchSel, day, refreshKey, tick, liveTick])

  // Plans keyed by appointment + the day's real sales, so a row can show its
  // planned promotions and tick the ones that actually sold.
  const planFor = useMemo(() => {
    const m = new Map()
    for (const pl of plans) m.set(planKey(pl.appointment_id, pl.patient_id), pl)
    return m
  }, [plans])
  const soldIndex = useMemo(() => groupSold(sold), [sold])
  // Names for the plans' ผู้รับผิดชอบ. Day-scoped — the month view shows counts only.
  const staffNames = useHuddleStaff(calView === 'day' ? branchSel : '', day)
  const planCountByDay = useMemo(() => {
    const m = new Map()
    for (const pl of plans) {
      const k = String(pl.plan_date || '').slice(0, 10)
      m.set(k, (m.get(k) || 0) + 1)
    }
    return m
  }, [plans])


  // Realtime: refetch the current view when an appointment in this branch changes.
  // Channel `appointment_update/{branch_id}` (nested, anonymous). Payload can be an
  // object {type, employee_id} or a string.
  // Debounced: since 2026-08-18 an appointment write can announce itself twice —
  // the endpoint's own emit (POST/PATCH /appointment) and the `patient_update -
  // appointment` table trigger, which is what makes status changes, deletes and
  // Bubble-side writes show up here at all. One refetch, not two.
  //
  // 🚨 OWN-WRITE EVENTS ARE NOT DISCARDED OUTRIGHT — they used to be, and the
  // calendar went stale on the writer's OTHER tab (reported 2026-09-12: change a
  // status on a patient page, the /appointments table never moves). The event
  // says WHO wrote it (`employee_id`, = the row's `modified_by`), not WHICH TAB,
  // so "my own edit already refetched locally" is true of the tab that made the
  // write and false of every other tab that person has open — and the front desk
  // keeps the calendar and a patient page side by side all day.
  // So: an own event is skipped only while THIS instance's own write-refetch is
  // still fresh (see `localWriteAt`). A second tab has no fresh local write, so
  // it refetches — one extra GET, only for the tabs that actually need it.
  const liveChannel = liveUpdates && branchSel ? `appointment_update/${branchSel}` : null
  const liveTimer = useRef(null)
  useEffect(() => () => clearTimeout(liveTimer.current), [])
  // When this instance last refetched because of a write made HERE: `refreshKey`
  // (the drawer saved, via the page) or `tick` (a drag/resize on the grid).
  const localWriteAt = useRef(0)
  useEffect(() => {
    if (refreshKey || tick) localWriteAt.current = Date.now()
  }, [refreshKey, tick])
  useXanoRealtime(liveChannel, (msg) => {
    const p = msg?.payload?.data ?? msg?.payload
    const emp = p && typeof p === 'object' ? p.employee_id : null
    const mine = emp != null && emp !== '' && me?.employee_id != null && String(emp) === String(me.employee_id)
    if (mine && Date.now() - localWriteAt.current < OWN_WRITE_GRACE_MS) return
    clearTimeout(liveTimer.current)
    liveTimer.current = setTimeout(() => setLiveTick((t) => t + 1), 350)
  })

  // Realtime: the patient-status chip on each block. The refetch above can NEVER
  // carry a status that a table trigger sets: creating a booking makes trigger 2
  // heal Error → มีนัดแล้ว, but the endpoint's and trigger 19's `appointment_update`
  // emits race it, so the calendar reloads the old status and nothing tells it
  // again — trigger 2 does not emit on this branch channel.
  // `recall_update` DOES get `{ type:"patient", action:"status", patient_id, status }`
  // from trigger 2 and from trigger 28 (every direct status write — the header pill,
  // PATCH /patient, the doctor portal, nightly task 7). Same rule as Huddle: the
  // event carries the new value, so patch the rows and refetch nothing. The
  // channel's other traffic is the recall lock, which has no `action` key.
  useXanoRealtime(liveUpdates ? 'recall_update' : null, (msg) => {
    const p = msg?.payload?.data ?? msg?.payload
    if (!p || typeof p !== 'object' || p.action !== 'status' || typeof p.status !== 'string') return
    const pid = Number(p.patient_id) || 0
    if (pid <= 0) return
    setRows((rs) => (rs.some((a) => Number(a.patient_id) === pid)
      ? rs.map((a) => (Number(a.patient_id) === pid ? { ...a, _patient: { ...(a._patient || {}), status: p.status } } : a))
      : rs))
  })

  // Drag/resize on the day grid → move (time + doctor) or extend (duration).
  async function commitMove(raw, { startMin, dur, docId }) {
    const start = dayMinToMs(day, startMin)
    const end = start + dur * 60000
    const isBlockRow = raw.block === true
    try {
      await patchJson(`/api/appointments/${raw.id}`, {
        date: day, start_date: start, end_date: end, duration: dur,
        doctor_id: Number(docId) || raw.doctor_id, branch_id: Number(branchSel) || raw.branch_id,
        appointmentlist_id: raw.appointmentlist_id,
        ...(isBlockRow ? { block: true } : {}),
      })
      toast.success(isBlockRow ? 'อัปเดตบล็อกแล้ว' : 'อัปเดตนัดหมายแล้ว')
    } catch { toast.error('อัปเดตไม่สำเร็จ') }
    setTick((t) => t + 1)
  }
  // Retry a failed month/day load: same refetch path a realtime event takes.
  const retryLoad = () => setLiveTick((t) => t + 1)

  // type alone → { type } (spec filter); + detail → adds id/detail (except filter)
  const treatment = treatType ? { type: treatType, id: treatId || null, detail: treatId ? (types.byId[treatId]?.detail || null) : null } : null

  /* ── การรักษา: one searchable list instead of a ประเภท → รายละเอียด cascade ──────
   * Staff know the treatment by name ("ฟอกสี", "รากเทียม") far more often than they
   * know which ประเภท it files under, and the cascade forced them to guess that first.
   * The options are CLEAR_LABEL + every entry in types.flat, grouped by ประเภท; the
   * selection is still stored as {treatType, treatId}, so every filter, the persisted
   * view state and the click-to-book duration below are untouched. */
  // CLEAR_LABEL is a bare string, so it renders ungrouped at the top, above the
  // first heading. Everything else carries its group.
  // Kit SearchSelect shape: CLEAR_LABEL is the '' option (ungrouped, top); each
  // entry keeps its unique "<Type> · <detail>" value, shows the short label in the
  // menu under its ประเภท heading, and the full value on the closed trigger.
  const treatOptions = useMemo(() => [
    { value: '', label: CLEAR_LABEL },
    ...types.flat.map((o) => ({ value: o.value, label: o.label, group: o.group, selectedLabel: o.value })),
  ], [types])
  const treatByLabel = useMemo(() => new Map(types.flat.map((o) => [o.value, o])), [types])
  const treatLabel = currentTreatmentLabel(treatType, treatId, types.byId)
  const pickTreat = (label) => {
    const o = treatByLabel.get(label)
    setTreatType(o?.type || '')
    setTreatId(o?.id || '')
    setCalView('month')
  }
  // What a click on an empty slot books. The treatment only counts while ตามการรักษา
  // is ON: the selects keep their value when you switch back to ทุกท่าน / ท่านเดิม, and a
  // filter nobody can see must not quietly pre-fill the booking. Its catalogue duration
  // is the default length (Ram, 2026-09-19) — the hover preview is drawn that tall and a
  // plain click books it. With no treatment it stays one 15-minute cell; the drawer
  // resets it to the catalogue default as soon as a รายละเอียด is picked.
  const bookTreatId = mode === 'spec' ? treatId : ''
  const bookDur = (bookTreatId && types.byId[bookTreatId]?.duration) || SNAP
  // ท่านเดิม → only active doctors (hide one only once we KNOW it's inactive).
  const prevActive = useMemo(() => prevDoctors.filter((d) => docInfo[String(d.id)]?.active !== false), [prevDoctors, docInfo])
  const prevSet = useMemo(() => new Set(prevActive.map((d) => String(d.id))), [prevActive])
  const allowedId = (did) => {
    if (mode === 'prev') return prevSet.has(String(did))
    if (mode === 'spec') return treatment ? doctorEligible(did, treatment, docInfo) : true
    return true
  }

  /* ---------- month grouping ---------- */
  const monthByDay = useMemo(() => {
    const map = {}
    for (const a of monthRows) {
      if (a.event_type !== 'inverse-background' || a.block === true) continue
      const did = String(a.doctor_id)
      if (!allowedId(did)) continue
      if (pickedDoc && pickedDoc !== did) continue
      if (mode === 'spec' && treatment && !shiftEligible(a, treatment, docInfo)) continue
      const key = a.date || (() => { const d = new Date(a.start_date + BKK); return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}` })()
      ;(map[key] ||= []).push({ id: did, min: bkkMin(a.start_date), end: bkkMin(a.end_date), name: a.doctor?.code || docInfo[did]?.code || a.titleDoctorTB || `ทันตแพทย์ #${did}` })
    }
    for (const k of Object.keys(map)) map[k].sort((p, q) => p.min - q.min)
    return map
  }, [monthRows, mode, treatment, docInfo, pickedDoc])

  /* ---------- day grouping ---------- */
  const columns = useMemo(() => {
    const byDoc = new Map()
    for (const a of rows) {
      const did = String(a.doctor_id)
      if (!allowedId(did)) continue
      if (pickedDoc && pickedDoc !== did) continue
      if (!byDoc.has(did)) byDoc.set(did, { id: did, name: a.doctor?.code || docInfo[did]?.code || a.titleDoctorTB || `ทันตแพทย์ #${did}`, windows: [], blocks: [], appts: [] })
      const col = byDoc.get(did)
      const s = bkkMin(a.start_date), e = bkkMin(a.end_date)
      if (a.event_type === 'inverse-background' && a.block !== true) {
        if (mode === 'spec' && treatment && !shiftEligible(a, treatment, docInfo)) continue
        col.windows.push({ s, e })
      } else if (a.block === true) {
        col.blocks.push({ s, e, detail: (a.details || a.remark || 'พัก').trim(), raw: a })
      } else if (!a.event_type) {
        // ยืนยันแล้ว = strictly C/C*. R48 is excluded on purpose even inside its
        // live 48h window — staff want this filter to be a clean list of true
        // confirmations. (R48's "counts as confirmed" applies to slot contention,
        // not to this filter. See data/appointmentStatuses.js.)
        if (confirmedOnly && !(a.status === 'C' || a.status === 'C*')) continue
        if (hideTreated && Number(a.treatment_count) > 0) continue
        const p = a._patient || {}
        const withNick = [p.name, p.nickname && `(${p.nickname})`].filter(Boolean).join(' ').trim()
        col.appts.push({
          s, e, raw: a, color: a.event_color || '#bfdbfe', status: a.status || '',
          r48: r48State(a),
          treatmentCount: a.treatment_count,
          name: (p.name || a.title || '').trim(),     // first name only → calendar block
          patient: withNick || (a.title || '').trim(), // name (nickname) → table/card/tooltip
          hn: p.HN || '',
          patientStatus: (p.status || '').trim(),      // patient case-status (NOT the appt status)
          type: (a._appointmentList?.type || '').trim(),
          detail: (a._appointmentList?.detail || '').trim(),
          note: (a.details || '').trim(),
          remark: (a.remark || '').trim(),
          upsell: upsellTags(a),                       // what the patient said สนใจ to on the SMS page
          scale: (Number(a.patient_id) || 0) > 0 ? scaleStatus(a) : null, // last-scaling status (a.scale_date from the dashboard addon)
        })
      }
    }
    return [...byDoc.values()].filter((c) => c.windows.length || c.appts.length).sort((x, y) => x.name.localeCompare(y.name, 'th'))
  }, [rows, mode, treatment, docInfo, pickedDoc, confirmedOnly, hideTreated])

  const chipsDoctors = useMemo(() => {
    const m = new Map()
    // Doctors on shift define the chip set (name + profile photo).
    for (const a of rows) {
      const did = String(a.doctor_id)
      if (!allowedId(did) || a.event_type !== 'inverse-background' || a.block === true) continue
      if (!m.has(did)) m.set(did, { id: did, name: a.doctor?.code || docInfo[did]?.code || a.titleDoctorTB || `ทันตแพทย์ #${did}`, img: a.doctor?.profile_img || docInfo[did]?.img || '', count: 0 })
      else if (!m.get(did).img && (a.doctor?.profile_img || docInfo[did]?.img)) m.get(did).img = a.doctor?.profile_img || docInfo[did]?.img
    }
    // Count patient appointments per doctor (respects the ยืนยันแล้ว filter).
    for (const a of rows) {
      if (a.event_type || a.block === true) continue
      const rec = m.get(String(a.doctor_id))
      if (!rec) continue
      if (confirmedOnly && !(a.status === 'C' || a.status === 'C*')) continue
      if (hideTreated && Number(a.treatment_count) > 0) continue
      rec.count++
    }
    return [...m.values()].sort((x, y) => x.name.localeCompare(y.name, 'th'))
  }, [rows, mode, treatment, docInfo, confirmedOnly, hideTreated])

  // Reset what narrows the doctors (not the branch — that is the scope, not a filter).
  const clearDoctorFilters = () => { setMode('all'); setTreatType(''); setTreatId(''); setPickedDoc('') }
  const canClearDoctorFilters = mode !== 'all' || !!treatType || !!pickedDoc
  const clearAllFilters = () => { clearDoctorFilters(); setConfirmedOnly(false); setHideTreated(false) }
  const chip = (on) => `px-2.5 py-1 rounded-full text-[13.5px] font-medium ring-1 whitespace-nowrap transition-colors ${on ? 'bg-brand-600 text-white ring-brand-600' : 'bg-white text-slate-600 ring-slate-200 hover:bg-slate-50'}`
  const shiftMonth = (d) => setMonthAnchor((m) => new Date(m.getFullYear(), m.getMonth() + d, 1))
  const shiftDay = (d) => { const [y, mo, dd] = day.split('-').map(Number); const dt = new Date(y, mo - 1, dd + d); setDay(`${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`) }
  const openDay = (key, docId = '') => { setDay(key); setPickedDoc(docId); setCalView('day') }

  // The filter bar rides with the day header instead of rendering above it:
  // DayView pins the two together, so สาขา/ทันตแพทย์ stay reachable however far down
  // the schedule you have scrolled.
  const toolbar = (
    <>
      {setupError && <ErrorState className="mb-3" title={setupError} onRetry={() => setSetupKey((k) => k + 1)} />}
      {/* Filter bar */}
      <FilterCard compact onClear={clearDoctorFilters} canClear={canClearDoctorFilters}
        aside={loading ? <Loading variant="inline" label="โหลด…" /> : null}>
        <div className="flex items-center gap-2 self-center">
          <Lbl>สาขา</Lbl>
          {/* mode 'form': real clinic branches only (no ทุกสาขา, no สนามเป้า) — the
              calendar always shows ONE branch. The อารีย์ fallback and the saved
              view still decide the default (effects above). */}
          <BranchSelect mode="form" className="w-[200px]" minMenuWidth={200}
            branches={branches} value={branchSel}
            onChange={(v) => { setBranchSel(String(v)); setPickedDoc('') }} />
        </div>

        <div className="flex items-center gap-2 self-center">
          <Lbl>ทันตแพทย์</Lbl>
          <Segmented value={mode} onChange={(k) => { setMode(k); setPickedDoc('') }}
            options={[{ value: 'all', label: 'ทุกท่าน' }, ...(allowPrevDoctors ? [{ value: 'prev', label: 'ท่านเดิม' }] : []), { value: 'spec', label: 'ตามการรักษา' }]} />
        </div>

        {mode === 'spec' && (
          <div className="flex items-center gap-2 self-center">
            <Lbl>การรักษา</Lbl>
            <SearchSelect
              className="w-[300px]"
              minMenuWidth={300}
              value={treatLabel}
              onChange={pickTreat}
              options={treatOptions}
              placeholder={CLEAR_LABEL}
              searchPlaceholder="ค้นหาการรักษา…"
            />
          </div>
        )}
      </FilterCard>

      {/* ท่านเดิม — the patient's previous doctors, with profile photos */}
      {mode === 'prev' && (
        <div className="mb-3 flex flex-wrap items-center gap-1.5">
          <span className="text-[12.5px] font-semibold uppercase tracking-wide text-slate-400 mr-1">ทันตแพทย์เดิมของคนไข้</span>
          {prevActive.length === 0
            ? <span className="text-[13.5px] text-slate-400">คนไข้ยังไม่เคยมีประวัติการรักษากับทันตแพทย์ท่านใด</span>
            : <>
                <button type="button" onClick={() => setPickedDoc('')} className={chip(pickedDoc === '')}>ทั้งหมด</button>
                {prevActive.map((d) => {
                  const sid = String(d.id)
                  const on = pickedDoc === sid
                  return (
                    <button key={sid} type="button" onClick={() => setPickedDoc(on ? '' : sid)} className={docTag(on)}>
                      <Avatar img={d.img} name={d.name} /> {d.name}
                      <span className={`ml-0.5 min-w-[18px] text-center rounded-full text-[11.5px] font-semibold px-1 py-px tabular-nums ${on ? 'bg-brand-100 text-brand-700' : 'bg-slate-100 text-slate-500'}`}>{d.count}</span>
                    </button>
                  )
                })}
              </>}
        </div>
      )}

    </>
  )

  return (
    <div>
      {calView === 'month'
        ? <>{toolbar}<MonthGrid anchor={monthAnchor} byDay={monthByDay} planCountByDay={planCountByDay} onPrev={() => shiftMonth(-1)} onNext={() => shiftMonth(1)} onToday={() => { const d = new Date(Date.now() + BKK); setMonthAnchor(new Date(d.getUTCFullYear(), d.getUTCMonth(), 1)) }} onOpenDay={openDay} branchSel={branchSel} loading={loading} error={loadError} onRetry={retryLoad} /></>
        : <DayView toolbar={toolbar} day={day} columns={columns} chipsDoctors={mode === 'prev' ? [] : chipsDoctors} pickedDoc={pickedDoc} setPickedDoc={setPickedDoc} chip={chip}
            dayView={dayView} setDayView={setDayView} confirmedOnly={confirmedOnly} setConfirmedOnly={setConfirmedOnly}
            hideTreated={hideTreated} setHideTreated={setHideTreated}
            onBack={() => { setCalView('month'); setPickedDoc('') }}
            onPrev={() => { shiftDay(-1); setPickedDoc('') }} onNext={() => { shiftDay(1); setPickedDoc('') }}
            onToday={() => { setDay(todayKey()); setPickedDoc('') }} setDay={(d) => { setDay(d); setPickedDoc('') }}
            branchSel={branchSel} loading={loading} rowsLen={rows.length} error={loadError} onRetry={retryLoad}
            onClearFilters={clearAllFilters}
            planFor={planFor} soldIndex={soldIndex} promoNames={promoNames} staffNames={staffNames} labIndex={labIndex}
            newDur={bookDur}
            onBook={(min, docId, dur) => onPickSlot?.(day, hm(min), bookTreatId || null, docId, branchSel, dur)} onEdit={onEditAppt} onMove={commitMove} />}
    </div>
  )
}

function Lbl({ children }) { return <span className="text-[12.5px] font-semibold uppercase tracking-wide text-slate-400">{children}</span> }
const docTag = (on) => `inline-flex items-center gap-1.5 pl-1 pr-2.5 py-1 rounded-full text-[13.5px] font-medium ring-1 transition-colors ${on ? 'bg-brand-50 text-brand-700 ring-brand-300' : 'bg-white text-slate-600 ring-slate-200 hover:bg-slate-50'}`
function Avatar({ img, name }) {
  if (img) return <img src={img} alt="" className="w-5 h-5 rounded-full object-cover ring-1 ring-black/5" referrerPolicy="no-referrer" />
  return <span className="w-5 h-5 rounded-full bg-slate-200 text-slate-600 text-[11.5px] font-semibold flex items-center justify-center">{nameInitial(name)}</span>
}

/* -------------------------------------------------------------------------- */
/* Month grid                                                                 */
/* -------------------------------------------------------------------------- */
function MonthGrid({ anchor, byDay, planCountByDay, onPrev, onNext, onToday, onOpenDay, branchSel, loading, error, onRetry }) {
  const y = anchor.getFullYear(), m = anchor.getMonth()
  const firstDow = (new Date(y, m, 1).getDay() + 6) % 7 // Monday-first offset
  const daysInMonth = new Date(y, m + 1, 0).getDate()
  const tKey = todayKey()
  const cells = []
  for (let i = 0; i < firstDow; i++) cells.push(null)
  for (let d = 1; d <= daysInMonth; d++) cells.push(d)
  while (cells.length % 7) cells.push(null)

  return (
    <div className="bg-white border border-slate-200/80 rounded-2xl overflow-hidden">
      <div className="flex items-center justify-center gap-1 px-3 py-3 border-b border-slate-100">
        <IconButton icon={ChevronLeft} label="เดือนก่อน" onClick={onPrev} />
        <div className="text-[15px] font-semibold text-slate-800 min-w-[160px] text-center">{fmtMonth(`${y}-${pad(m + 1)}-01`, { long: true })}</div>
        <IconButton icon={ChevronRight} label="เดือนถัดไป" onClick={onNext} />
        <Button variant="ghost" size="sm" className="ml-1" onClick={onToday}>วันนี้</Button>
      </div>
      <div className="grid grid-cols-7 border-b border-slate-100 text-center text-[13.5px] font-medium text-slate-500">
        {WEEK_HEAD.map((d) => <div key={d} className="py-2">{d}</div>)}
      </div>
      {!branchSel ? (
        <EmptyState variant="plain" title="เลือกสาขาก่อน" />
      ) : error ? (
        <div className="p-4"><ErrorState message={error} onRetry={onRetry} /></div>
      ) : (
        <div className="grid grid-cols-7">
          {cells.map((d, i) => {
            if (d == null) return <div key={i} className="min-h-[120px] border-b border-r border-slate-50 bg-slate-50/30" />
            const key = `${y}-${pad(m + 1)}-${pad(d)}`
            const items = byDay[key] || []
            const isToday = key === tKey
            return (
              <div key={i} role="button" tabIndex={0} aria-label={`เปิดวันที่ ${fmtDateText(key)}`} onClick={() => onOpenDay(key)}
                onKeyDown={(e) => {
                  // Enter/Space on the CELL opens the day; the doctor buttons inside
                  // handle their own keys (target check keeps them from double-firing).
                  if (e.target !== e.currentTarget) return
                  if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpenDay(key) }
                }}
                className="min-h-[120px] border-b border-r border-slate-50 p-1.5 text-left align-top hover:bg-brand-50/40 transition-colors flex flex-col gap-1 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-300">
                <div className="flex items-center justify-between gap-1">
                  <span className={`text-[13.5px] font-semibold ${isToday ? 'inline-flex items-center justify-center w-6 h-6 rounded-full bg-brand-600 text-white' : 'text-slate-700'}`}>{d}</span>
                  {planCountByDay?.get(key) > 0 && (
                    <span title={`มีแผนอัปเซล ${planCountByDay.get(key)} รายการ`}
                      className="inline-flex items-center gap-0.5 rounded px-1 py-0.5 text-[10.5px] font-semibold text-brand-700 bg-brand-50 ring-1 ring-brand-200 tabular-nums">
                      <Tag size={9} />{planCountByDay.get(key)}
                    </span>
                  )}
                </div>
                <div className="space-y-0.5">
                  {items.map((it, k) => (
                    <button key={k} type="button" title={`ดูเฉพาะ ${it.name}`}
                      onClick={(e) => { e.stopPropagation(); onOpenDay(key, it.id) }}
                      className="block w-full text-left text-[12px] leading-tight rounded bg-sky-50 hover:bg-sky-100 px-1 py-0.5 truncate transition-colors">
                      <span className="font-semibold text-slate-700">{it.name}</span> <span className="text-slate-500">{hm(it.min)}–{hm(it.end)}</span>
                    </button>
                  ))}
                </div>
              </div>
            )
          })}
        </div>
      )}
      {loading && <div className="px-4 py-2"><Loading variant="inline" label="โหลดตารางทันตแพทย์…" /></div>}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Day view (per-doctor grid)                                                 */
/* -------------------------------------------------------------------------- */
function DayView({ toolbar, staffNames, day, columns, chipsDoctors, pickedDoc, setPickedDoc, chip, dayView, setDayView, confirmedOnly, setConfirmedOnly, hideTreated, setHideTreated, onBack, onPrev, onNext, onToday, setDay, branchSel, loading, rowsLen, error, onRetry, onClearFilters, newDur = SNAP, onBook, onEdit, onMove, planFor, soldIndex, promoNames, labIndex }) {
  const [yy, mo, dd] = day.split('-').map(Number)
  // "อังคาร 8 ส.ค. 2026" — full weekday (local, kit has only the short one) + kit date text (ค.ศ.).
  const label = `${TH_DAYS_FULL[new Date(yy, mo - 1, dd).getDay()]} ${fmtDateText(day)}`
  // Fit the grid to the day's actual hours — timetable windows (inverse-background)
  // first, plus appts/blocks so nothing is clipped (e.g. Tooth Taxi starts 08:00).
  const [dayStart, dayEnd] = useMemo(() => {
    const starts = [], ends = []
    for (const c of columns) {
      for (const w of c.windows) { starts.push(w.s); ends.push(w.e) }
      for (const b of c.blocks) { starts.push(b.s); ends.push(b.e) }
      for (const a of c.appts) { starts.push(a.s); ends.push(a.e) }
    }
    if (!starts.length) return [DAY_START, DAY_END]
    const lo = Math.floor(Math.min(...starts) / 60) * 60
    const hi = Math.ceil(Math.max(...ends) / 60) * 60
    return [Math.min(lo, hi - 60), Math.max(hi, lo + 60)]
  }, [columns])
  const ticks = []; for (let t = dayStart; t <= dayEnd; t += 15) ticks.push(t)
  const gridH = (dayEnd - dayStart) * PXM
  const appts = columns.flatMap((c) => c.appts.map((a) => ({ ...a, doctor: c.name }))).sort((x, y) => x.s - y.s)
  const canTable = columns.length === 1                                  // table only for one date + one doctor
  const effView = (dayView === 'table' && canTable) ? 'table' : 'calendar'

  // The doctor row freezes directly UNDER the sticky toolbar, whose height is
  // not a constant (filters wrap, the chips row appears only when there are
  // chips) — so measure it rather than guess.
  const toolbarRef = useRef(null)
  const [toolbarH, setToolbarH] = useState(0)
  useEffect(() => {
    const el = toolbarRef.current
    if (!el) return
    const read = () => setToolbarH(el.offsetHeight)
    read()
    if (typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(read)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  // Doctor header ↔ grid horizontal scroll. They are two boxes now (see the
  // card below), so scrolling the grid sideways has to carry the names along.
  const headRef = useRef(null)
  const bodyRef = useRef(null)
  const syncHead = () => { if (headRef.current && bodyRef.current) headRef.current.scrollLeft = bodyRef.current.scrollLeft }

  // ---- drag (move) / resize (extend duration) on the day grid ----
  const colsRef = useRef(null)
  const pendingRef = useRef(null)   // {appt, fromDocId, startX, startY, pointerId, mode}
  const suppressClick = useRef(false)
  const [drag, setDrag] = useState(null)
  // A dropped move/resize waits here for ยืนยัน (Ram, 2026-10-20): a slip of the mouse
  // used to rebook a patient silently. Nothing is written until the dialog is confirmed;
  // the ghost stays drawn at the drop point meanwhile, so staff see what they are agreeing to.
  const [held, setHeld] = useState(null)
  const [moving, setMoving] = useState(false)
  const ghost = drag || held
  async function confirmHeld() {
    const h = held
    if (!h) return
    setMoving(true)
    try { await onMove?.(h.raw, { startMin: h.gMin, dur: h.gDur, docId: h.docId }) } finally {
      setMoving(false)
      setHeld(null)
    }
  }

  const snap = (m) => Math.round(m / SNAP) * SNAP
  function rawMinOf(e) {
    const r = colsRef.current.getBoundingClientRect()
    return Math.max(dayStart, Math.min(dayEnd, dayStart + (e.clientY - r.top) / PXM))
  }
  function idxOf(e) {
    const r = colsRef.current.getBoundingClientRect()
    const colW = r.width / Math.max(columns.length, 1)
    return Math.max(0, Math.min(columns.length - 1, Math.floor((e.clientX - r.left) / colW)))
  }
  function apptDown(e, appt, fromDocId, mode) {
    suppressClick.current = false // fresh interaction
    pendingRef.current = { appt, fromDocId, startX: e.clientX, startY: e.clientY, pointerId: e.pointerId, mode, pdMin: rawMinOf(e) }
  }
  // pointer-down on an empty in-window slot → potential "create" (click or drag-select)
  function emptyDown(e, colId, startMin) {
    suppressClick.current = false
    pendingRef.current = { mode: 'create', fromDocId: colId, startMin, startX: e.clientX, startY: e.clientY, pointerId: e.pointerId }
  }
  function onColsMove(e) {
    if (drag) {
      if (drag.mode === 'move') {
        let ns = snap(rawMinOf(e) + drag.grab)               // grab from pointer-down → no jump
        ns = Math.max(dayStart, Math.min(dayEnd - drag.dur, ns))
        setDrag((d) => ({ ...d, gMin: ns, idx: idxOf(e) }))
      } else if (drag.mode === 'resize') {
        const ne = Math.min(dayEnd, Math.max(drag.start + SNAP, snap(rawMinOf(e))))
        setDrag((d) => ({ ...d, gDur: ne - drag.start }))
      } else { // create: drag-select a range
        const cur = Math.max(dayStart, Math.min(dayEnd, snap(rawMinOf(e))))
        const lo = Math.min(drag.start, cur), hi = Math.max(drag.start, cur)
        setDrag((d) => ({ ...d, gMin: lo, gDur: Math.max(hi - lo, SNAP) }))
      }
      return
    }
    const p = pendingRef.current
    if (p && (Math.abs(e.clientX - p.startX) > 4 || Math.abs(e.clientY - p.startY) > 4)) {
      try { colsRef.current.setPointerCapture(p.pointerId) } catch { /* */ }
      const idx = columns.findIndex((c) => c.id === p.fromDocId)
      if (p.mode === 'create') setDrag({ mode: 'create', fromDocId: p.fromDocId, start: p.startMin, gMin: p.startMin, gDur: SNAP, idx })
      else {
        const dur = p.appt.e - p.appt.s
        if (p.mode === 'resize') setDrag({ mode: 'resize', raw: p.appt.raw, start: p.appt.s, origDur: dur, gMin: p.appt.s, gDur: dur, idx, fromDocId: p.fromDocId })
        else setDrag({ mode: 'move', raw: p.appt.raw, dur, grab: p.appt.s - p.pdMin, origStart: p.appt.s, origDoc: p.fromDocId, gMin: p.appt.s, gDur: dur, idx, fromDocId: p.fromDocId })
      }
    }
  }
  function onColsUp() {
    const d = drag
    const p = pendingRef.current
    pendingRef.current = null
    if (d) {
      setDrag(null)
      if (d.mode !== 'create') suppressClick.current = true
      if (d.mode === 'move') {
        const docId = columns[d.idx]?.id || d.fromDocId
        if (d.gMin !== d.origStart || docId !== d.origDoc) {
          setHeld({ ...d, gDur: d.dur, docId, origMin: d.origStart, origDur: d.dur })
        }
      } else if (d.mode === 'resize') {
        if (d.gDur !== d.origDur) setHeld({ ...d, gMin: d.start, docId: d.fromDocId, origDoc: d.fromDocId, origMin: d.start })
      } else { // create via drag-select → carry the dragged duration
        onBook?.(d.gMin, columns[d.idx]?.id || d.fromDocId, d.gDur)
      }
      return
    }
    // plain click → the chosen treatment's catalogue length, else one 15-min cell
    if (p && p.mode === 'create') onBook?.(p.startMin, p.fromDocId, newDur)
  }

  // ตาราง (one doctor, one day). Row click edits the appointment; the patient
  // opens only from their name / HN (decision #4), landing on นัดหมาย.
  const tableColumns = [
    {
      key: 'time', label: 'เวลา', width: '92px',
      render: (a) => (
        <span className="whitespace-nowrap tabular-nums text-slate-700">
          {hm(a.s)}
          <span className="block text-[12px] text-slate-400 leading-tight">{Math.max(a.e - a.s, 0)} นาที</span>
        </span>
      ),
    },
    {
      key: 'patient', label: 'คนไข้',
      render: (a) => (
        <div className="min-w-[150px]">
          {a.patient
            ? <PatientLink patientId={a.raw.patient_id} context="appointment" className="font-medium">{a.patient}</PatientLink>
            : <span className="text-slate-400">—</span>}
          {a.scale?.overdue && <ScaleTag status={a.scale} compact className="ml-1 align-middle" />}
          {a.hn && (
            <div className="mt-0.5 text-[12px]">
              <PatientLink patientId={a.raw.patient_id} context="appointment" className="tabular-nums text-slate-500 hover:text-brand-700">{a.hn}</PatientLink>
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'patientStatus', label: 'สถานะคนไข้',
      render: (a) => {
        const tone = a.patientStatus ? STATUS_TONES[statusTone(a.patientStatus)] : null
        return tone
          ? <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[11px] font-medium ring-1 whitespace-nowrap ${tone.pill}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${tone.dot}`} />{a.patientStatus}
            </span>
          : <span className="text-slate-400">—</span>
      },
    },
    {
      key: 'detail', label: 'รายละเอียด',
      render: (a) => {
        const typeLine = [a.type, a.detail].filter(Boolean).join(' · ')
        // A visit whose only detail IS the lab still needs the tag line.
        const labSum = labIndex ? apptLabSummary(labIndex, a.raw) : null
        const hasDetail = typeLine || a.note || a.remark || labSum
        return (
          <>
            {a.note && <div className="text-slate-700 leading-snug">{a.note}</div>}
            {a.remark && <div className="text-[13px] text-slate-500 leading-snug mt-0.5">{a.remark}</div>}
            {(typeLine || a.upsell.length > 0 || labSum) && (
              <div className="mt-1.5 flex flex-wrap items-center gap-1">
                {typeLine && <span className="inline-block rounded border border-slate-200 px-1.5 py-0.5 text-[11.5px] text-slate-500">{typeLine}</span>}
                <ApptLabFlag index={labIndex} appt={a.raw} size="sm" />
                <UpsellTags tags={a.upsell} />
              </div>
            )}
            {!hasDetail && a.upsell.length === 0 && <span className="text-slate-400">—</span>}
          </>
        )
      },
    },
    // Own column: the plan is a decision the team made, not a detail of the
    // appointment, and buried under the notes it was easy to miss. The header
    // names it, so the chips drop their inline label.
    {
      key: 'plan', label: 'แผนอัปเซล', width: '200px',
      render: (a) => {
        const plan = planFor?.get(planKey(a.raw?.id, a.raw?.patient_id))
        return plan
          ? <PlanChips
              label={false}
              ids={plan.promotion_id || []}
              hits={planOutcome(plan, soldIndex).hits}
              promoNames={promoNames}
              assignees={plan.assignee_id || []}
              staffNames={staffNames}
            />
          : <span className="text-slate-400">—</span>
      },
    },
    {
      key: 'status', label: 'สถานะ', align: 'center', width: '56px',
      render: (a) => (a.status
        ? <span className="whitespace-nowrap">
            <span title={[STATUS_TH[a.status] || a.status, a.r48?.is && a.r48.hint].filter(Boolean).join(' · ')}
              className={`inline-flex items-center justify-center min-w-[30px] px-1.5 py-0.5 rounded-md text-[12.5px] font-bold ring-1 ${a.r48?.expired ? R48_EXPIRED_CLS : codeCls(a.status)}`}>{a.status}</span>
            {a.r48?.is && <span className={`block mt-0.5 text-[11px] leading-none ${a.r48.expired ? 'text-slate-400' : 'text-amber-600'}`}>{a.r48.hint}</span>}
          </span>
        : <span className="text-slate-400">—</span>),
    },
  ]

  const isToday = day === todayKey()

  return (
    <div>
      {/* Filters + day nav + doctor chips pin to the top of the scroll area
          (<main> in Layout), so the controls stay reachable however far down the
          day you scroll. z-40 clears the grid's own z-30 drag overlay and stays
          under the drawers at z-50. */}
      <div ref={toolbarRef} className="sticky top-0 z-40 bg-[#fafbfc] pt-3">
      {toolbar}
      {/* Day nav (centered) — back on the left, view toggle on the right */}
      <div className="relative flex items-center justify-center mb-2.5 min-h-[36px]">
        <Button variant="soft" size="sm" icon={ChevronLeft} className="absolute left-0" onClick={onBack}>เดือน</Button>
        <div className="inline-flex items-center gap-1">
          <IconButton icon={ChevronLeft} label="วันก่อน" onClick={onPrev} />
          <div className="text-[15px] font-semibold text-slate-800 min-w-[150px] text-center">{label}</div>
          <IconButton icon={ChevronRight} label="วันถัดไป" onClick={onNext} />
          {/* Doubles as a state light: tinted when the day on screen IS today, plain
              slate when it is a jump-back action. */}
          <button type="button" onClick={onToday} aria-current={isToday ? 'date' : undefined}
            className={`ml-1 px-2.5 py-1.5 rounded-lg text-[13.5px] font-medium transition-colors ${
              isToday
                ? 'bg-brand-50 text-brand-700 ring-1 ring-brand-200 cursor-default'
                : 'text-slate-500 hover:bg-slate-100'}`}>วันนี้</button>
        </div>
        <div className="absolute right-0 flex items-center gap-2">
          {effView === 'table' && columns.length === 1 && (
            <Button variant="secondary" size="sm" icon={Printer}
              to={`/appointments/print?branch_id=${encodeURIComponent(branchSel)}&date=${encodeURIComponent(day)}&doctor_id=${encodeURIComponent(columns[0].id)}`}
              target="_blank" rel="noopener noreferrer" title="เปิดหน้าพิมพ์ตาราง (แท็บใหม่)">
              พิมพ์
            </Button>
          )}
          {/* ตาราง only exists for one date + one doctor (canTable), as before. */}
          <ViewToggle value={effView} onChange={setDayView} views={canTable ? ['calendar', 'table'] : ['calendar']} />
        </div>
      </div>

      {/* Filters row — status · hide-treated · slot key */}
      <div className="flex items-center gap-2 mb-2">
        <Segmented size="sm" value={confirmedOnly} onChange={setConfirmedOnly}
          options={[{ value: false, label: 'ทั้งหมด' }, { value: true, label: 'ยืนยันแล้ว' }]} />
        <Switch size="sm" className="ml-2" checked={hideTreated} onChange={setHideTreated} label="ซ่อนที่รักษาแล้ว" />
        {effView === 'calendar' && (
          <span className="ml-auto hidden sm:flex items-center gap-3 text-[12px] text-slate-400">
            <span className="inline-flex items-center gap-1"><span className="w-3 h-2 rounded-sm bg-white ring-1 ring-slate-200" /> ว่าง</span>
            <span className="inline-flex items-center gap-1"><span className="w-3 h-2 rounded-sm bg-slate-100" /> นอกเวลา</span>
            <span className="inline-flex items-center gap-1"><span className="w-3 h-2 rounded-sm bg-slate-300" /> บล็อก</span>
          </span>
        )}
      </div>

      {/* Doctor chips — single horizontal scroll row (no wrap) */}
      {chipsDoctors.length > 0 && (
        <div className="flex items-center gap-1.5 overflow-x-auto pt-1 pb-2 mb-1 -mx-0.5 px-0.5 [scrollbar-width:thin]">
          {chipsDoctors.map((d) => {
            const on = !pickedDoc || pickedDoc === d.id
            return (
              <button key={d.id} type="button" onClick={() => setPickedDoc(pickedDoc === d.id ? '' : d.id)} className={`${docTag(on)} flex-none whitespace-nowrap`}>
                <Avatar img={d.img} name={d.name} /> {d.name}
                <span className={`ml-0.5 min-w-[18px] text-center rounded-full text-[11.5px] font-semibold px-1 py-px tabular-nums ${on ? 'bg-brand-100 text-brand-700' : 'bg-slate-100 text-slate-500'}`}>{d.count}</span>
              </button>
            )
          })}
        </div>
      )}
      </div>

      {/* Exclusive states: error · first load · nothing booked · filtered out. A
          failed load clears the rows (as before), so it can never sit beside a grid. */}
      {columns.length === 0 ? (
        error ? <ErrorState message={error} onRetry={onRetry} />
          : !branchSel ? <EmptyState title="เลือกสาขาก่อน" />
          : loading ? <Loading />
          : rowsLen === 0 ? <EmptyState title="ไม่มีตารางทันตแพทย์/นัดในวันนี้" />
          : <NoMatchState variant="card" title="ไม่มีทันตแพทย์ที่ตรงกับตัวกรองในวันนี้" onClear={onClearFilters} />
      ) : effView === 'calendar' ? (
        // The CARD must not set overflow: any overflow here would become the
        // sticky row's scrollport and freeze it to the card instead of the page.
        <div className="bg-white border border-slate-200/80 rounded-2xl">
          {/* Doctor names stay put under the toolbar as the day scrolls. They sit
              in their own horizontal scroller because the grid's own overflow-x
              makes IT the scrollport for anything inside it — a sticky header in
              there would never move. z-[35]: over the grid's drag overlay (z-30),
              under the toolbar it parks against (z-40). */}
          <div ref={headRef} className="sticky z-[35] overflow-hidden rounded-t-2xl bg-white border-b border-slate-100" style={{ top: Math.max(0, toolbarH - 1) }}>
            <div className="flex">
              <div className="flex-none w-14 border-r border-slate-100" />
              <div className="flex flex-1">
                {columns.map((col) => {
                  const on = pickedDoc === col.id
                  return (
                    <button key={col.id} type="button" title="คลิกเพื่อแสดงเฉพาะทันตแพทย์ท่านนี้"
                      onClick={() => setPickedDoc(on ? '' : col.id)}
                      className={`flex-1 min-w-[140px] h-9 px-2 text-[13.5px] font-semibold truncate border-r border-slate-100 last:border-r-0 transition-colors ${
                        on ? 'bg-brand-50 text-brand-700' : 'bg-white text-slate-700 hover:bg-slate-50'}`}>{col.name}</button>
                  )
                })}
              </div>
            </div>
          </div>
          <div ref={bodyRef} onScroll={syncHead} className="overflow-x-auto overflow-y-hidden rounded-b-2xl">
          <div className="flex">
            <div className="flex-none w-14 border-r border-slate-100">
              <div className="relative" style={{ height: gridH }}>
                {ticks.filter((t) => t % 60 === 0).map((t) => (
                  <div key={t} className="absolute left-0 right-0 -translate-y-1/2 text-right pr-1.5 tabular-nums text-[12px] text-slate-500 font-medium" style={{ top: (t - dayStart) * PXM }}>{hm(t)}</div>
                ))}
              </div>
            </div>
            <div ref={colsRef} className="relative flex flex-1" style={{ touchAction: 'none' }}
              onPointerMove={onColsMove} onPointerUp={onColsUp} onPointerCancel={onColsUp}>
              {columns.map((col) => (
                <DoctorColumn key={col.id} col={col} gridH={gridH} ticks={ticks} dayStart={dayStart} dayEnd={dayEnd}
                  focused={pickedDoc === col.id}
                  planFor={planFor} soldIndex={soldIndex} promoNames={promoNames} staffNames={staffNames} labIndex={labIndex}
                  onEdit={onEdit} onApptDown={apptDown} onEmptyDown={emptyDown}
                  newDur={newDur} dragging={!!ghost}
                  suppressClickRef={suppressClick} draggingId={ghost && ghost.raw ? String(ghost.raw.id) : null} />
              ))}
              {ghost && columns.length > 0 && (
                <div className="absolute pointer-events-none z-30 rounded ring-2 ring-brand-500 bg-brand-500/25 text-[11px] font-semibold text-brand-800 px-1 overflow-hidden flex items-start"
                  style={{ left: `calc(${(ghost.idx * 100) / columns.length}% + 10px)`, width: `calc(${100 / columns.length}% - 11px)`, top: (ghost.gMin - dayStart) * PXM, height: Math.max(ghost.gDur * PXM, 16) }}>
                  {hm(ghost.gMin)}–{hm(ghost.gMin + ghost.gDur)}
                </div>
              )}
            </div>
          </div>
          </div>
        </div>
      ) : (
        <DataTable
          columns={tableColumns}
          rows={appts}
          rowKey={(a, i) => a.raw?.id ?? i}
          onRowClick={(a) => onEdit?.(a.raw)}
          rowAccent={(a) => a.color}
          loading={loading}
          filtered={confirmedOnly || hideTreated}
          onClearFilters={onClearFilters}
          empty={{ title: 'ยังไม่มีนัดที่จองในวันนี้' }}
        />
      )}
      {/* What the status badges on the blocks/rows mean — both views show them,
          so it sits under both, at the very bottom of the day. */}
      {columns.length > 0 && <ApptStatusLegend className="mt-4" />}
      {held && <MoveConfirm held={held} columns={columns} busy={moving} onConfirm={confirmHeld} onCancel={() => setHeld(null)} />}
    </div>
  )
}

// The before → after of a dropped move/resize, as labelled lines — only what changed is
// shown as a change, so "เลื่อนเวลา" and "ย้ายทันตแพทย์" read differently at a glance.
function MoveConfirm({ held, columns, busy, onConfirm, onCancel }) {
  const raw = held.raw || {}
  const isBlock = raw.block === true
  const p = raw._patient || {}
  const who = isBlock ? (raw.details || raw.title || 'บล็อกเวลา') : ([p.name, p.nickname && `(${p.nickname})`].filter(Boolean).join(' ') || raw.title || `นัดหมาย #${raw.id}`)
  const docName = (id) => columns.find((c) => c.id === id)?.name || `ทันตแพทย์ #${id}`
  const span = (m, d) => `${hm(m)}–${hm(m + d)} น.`
  const lines = [
    { label: 'เวลา', from: span(held.origMin, held.origDur), to: span(held.gMin, held.gDur) },
    { label: 'ทันตแพทย์', from: docName(held.origDoc), to: docName(held.docId) },
  ]
  if (held.gDur !== held.origDur) lines.push({ label: 'ระยะเวลา', from: `${held.origDur} นาที`, to: `${held.gDur} นาที` })
  return (
    <ConfirmDialog
      title={held.mode === 'resize' ? (isBlock ? 'เปลี่ยนระยะเวลาบล็อก?' : 'เปลี่ยนระยะเวลานัด?') : (isBlock ? 'ย้ายบล็อกเวลา?' : 'ย้ายนัดหมาย?')}
      message={(
        <div className="space-y-1.5">
          <div className="font-medium text-slate-800">{who}</div>
          {lines.map((l) => (
            <div key={l.label} className="flex gap-2 text-[13px]">
              <span className="w-[68px] flex-none text-slate-400">{l.label}</span>
              {l.from === l.to
                ? <span className="text-slate-600">{l.to}</span>
                : <span><span className="text-slate-400 line-through">{l.from}</span> → <span className="font-semibold text-slate-900">{l.to}</span></span>}
            </div>
          ))}
        </div>
      )}
      confirmLabel="ยืนยันการเปลี่ยน"
      busy={busy}
      onConfirm={onConfirm}
      onCancel={onCancel}
    />
  )
}

function DoctorColumn({ col, gridH, ticks, dayStart, dayEnd, focused, planFor, soldIndex, promoNames, staffNames, labIndex, onEdit, onApptDown, onEmptyDown, newDur = SNAP, dragging, suppressClickRef, draggingId }) {
  const ref = useRef(null)
  const top = (m) => (m - dayStart) * PXM
  const inWindow = (m) => col.windows.some((w) => m >= w.s && m < w.e)
  // The 15-minute cell under the pointer. FLOOR, not round: the hover preview and the
  // click must name the same cell, and the one you point at is the one you mean.
  const minAt = (clientY) => dayStart + Math.floor(((clientY - ref.current.getBoundingClientRect().top) / PXM) / SNAP) * SNAP
  // pointer-down on an in-window slot → start a "create" (click or drag-select).
  // Overlapping an existing event is allowed (start in the left gutter strip).
  function bodyDown(e) {
    if (!ref.current) return
    const m = minAt(e.clientY)
    if (!inWindow(m)) return
    onEmptyDown?.(e, col.id, m)
  }
  // Hover preview (Ram, 2026-09-19): what a click here would book — the cell's time,
  // drawn as tall as the booking it creates (`newDur`), with the doctor named under it.
  // Mouse only: a touch has no hover, and its move events belong to the drag.
  const [hover, setHover] = useState(null)
  function bodyMove(e) {
    if (e.pointerType !== 'mouse' || !ref.current) return
    const m = minAt(e.clientY)
    // Over an appointment or a block the click opens THAT, so no preview there — the
    // left gutter strip beside them is still "book over it", and previews like it.
    const ok = !e.target.closest('[data-cal-item]') && inWindow(m)
    setHover((h) => (ok ? (h === m ? h : m) : null))
  }
  // inverse-background: grey the time OUTSIDE the doctor's working window(s).
  const greys = []
  const wins = [...col.windows].sort((a, b) => a.s - b.s)
  const merged = []
  for (const w of wins) { const last = merged[merged.length - 1]; if (last && w.s <= last.e) last.e = Math.max(last.e, w.e); else merged.push({ ...w }) }
  let cur = dayStart
  for (const w of merged) { const ws = Math.max(w.s, dayStart), we = Math.min(w.e, dayEnd); if (ws > cur) greys.push({ s: cur, e: ws }); cur = Math.max(cur, we) }
  if (cur < dayEnd) greys.push({ s: cur, e: dayEnd })

  return (
    <div className={`flex-1 min-w-[140px] border-r border-slate-100 last:border-r-0 ${focused ? 'ring-2 ring-brand-300 ring-inset' : ''}`}>
      <div ref={ref} className="relative cursor-pointer bg-white" style={{ height: gridH, touchAction: 'none' }} onPointerDown={bodyDown}
        onPointerMove={bodyMove} onPointerLeave={() => setHover(null)}>
        {greys.map((g, i) => <div key={`g${i}`} className="absolute left-0 right-0 bg-slate-100" style={{ top: top(g.s), height: (g.e - g.s) * PXM }} />)}
        {ticks.map((t) => <div key={t} className={`absolute left-0 right-0 border-t ${t % 60 === 0 ? 'border-slate-100' : 'border-slate-50'}`} style={{ top: top(t) }} />)}
        {col.blocks.map((b, i) => {
          const dim = draggingId && b.raw && String(b.raw.id) === draggingId
          return (
            <div key={`b${i}`} data-cal-item title={`${b.detail} · ลาก/ปรับขนาด · คลิกเพื่อแก้ไข`}
              onPointerDown={(e) => { e.stopPropagation(); onApptDown?.(e, b, col.id, 'move') }}
              onClick={(e) => { e.stopPropagation(); if (suppressClickRef?.current) { suppressClickRef.current = false; return } if (b.raw) onEdit?.(b.raw) }}
              className={`absolute left-0.5 right-0.5 rounded bg-slate-300/80 hover:bg-slate-400/80 text-slate-700 text-[11px] leading-tight px-1 overflow-hidden cursor-grab active:cursor-grabbing select-none transition-colors ${dim ? 'opacity-40' : ''}`}
              style={{ top: top(b.s), height: Math.max((b.e - b.s) * PXM, 14), touchAction: 'none' }}>
              🔒 {b.detail}
              <div onPointerDown={(e) => { e.stopPropagation(); onApptDown?.(e, b, col.id, 'resize') }}
                className="absolute left-0 right-0 bottom-0 h-2 cursor-ns-resize" />
            </div>
          )
        })}
        {layoutAppts(col.appts).map((a, i) => {
          // A block is far too small for the upsell chips the table shows, so the
          // plan collapses to one tag glyph — green once any planned promotion
          // actually sold that day. The names live in the tooltip.
          const plan = planFor?.get(planKey(a.raw?.id, a.raw?.patient_id))
          const planIds = plan?.promotion_id || []
          const planWon = planIds.length > 0 && planOutcome(plan, soldIndex).hits.size > 0
          const planNames = planIds.map((id) => promoNames?.get?.(Number(id)) || `#${id}`).join(' / ')
          const planWho = assigneeNames(plan?.assignee_id, staffNames).join(' / ')
          const tip = [`${hm(a.s)}–${hm(a.e)}`, a.patient && (a.hn ? `${a.patient} (HN ${a.hn})` : a.patient), [a.type, a.detail].filter(Boolean).join(' / '), a.note, planNames && `อัปเซล: ${planNames}${planWon ? ' · ขายได้แล้ว' : ''}`, planWho && `ผู้รับผิดชอบ: ${planWho}`].filter(Boolean).join(' · ')
          const dim = draggingId && String(a.raw.id) === draggingId
          return (
            <div key={`a${i}`} data-cal-item title={tip}
              onPointerDown={(e) => { e.stopPropagation(); onApptDown?.(e, a, col.id, 'move') }}
              onClick={(e) => { e.stopPropagation(); if (suppressClickRef?.current) { suppressClickRef.current = false; return } onEdit?.(a.raw) }}
              className={`absolute rounded text-left text-[11px] leading-tight px-1 py-0.5 overflow-hidden ring-1 ring-black/5 hover:brightness-95 cursor-grab active:cursor-grabbing select-none ${dim ? 'opacity-40' : ''}`}
              style={{ top: top(a.s), height: Math.max((a.e - a.s) * PXM, 16), backgroundColor: a.color, color: '#0f172a', touchAction: 'none', left: `calc(10px + ${a.lane} * (100% - 11px) / ${a.cols})`, width: `calc((100% - 11px) / ${a.cols} - 2px)` }}>
              <div className="flex items-start gap-0.5">
                <div className="truncate min-w-0 flex-1">
                  {planIds.length > 0 && (
                    <Tag size={9} strokeWidth={2.75} className={`inline align-[-1px] mr-0.5 ${planWon ? 'text-emerald-600' : 'text-brand-700'}`} />
                  )}
                  <span className="font-semibold">{a.name || '—'}</span>
                  {(() => {
                    const rest = [[a.type, a.detail].filter(Boolean).join(' '), a.note, a.status].filter(Boolean).join(' · ')
                    return rest ? <span className="opacity-80"> · {rest}</span> : null
                  })()}
                </div>
                {/* The patient said สนใจ to an upgrade on the SMS page. Outside the
                    truncating line, so a narrow block cuts the notes — never the mark. */}
                {a.upsell.length > 0 && (
                  <span className="flex-none" title={`คนไข้สนใจอัปเกรด: ${a.upsell.join(' / ')}`}>{UPSELL_MARK}</span>
                )}
                {/* Same reasoning as the upsell mark: outside the truncation, because a
                    crown that has not arrived is the last thing a narrow block should drop. */}
                <span className="flex-none"><ApptLabFlag index={labIndex} appt={a.raw} bare /></span>
              </div>
              {/* bottom resize handle → extend duration */}
              <div onPointerDown={(e) => { e.stopPropagation(); onApptDown?.(e, a, col.id, 'resize') }}
                className="absolute left-0 right-0 bottom-0 h-2 cursor-ns-resize" />
            </div>
          )
        })}
        {hover != null && !dragging && (
          <div className="absolute left-0.5 right-0.5 z-20 pointer-events-none" style={{ top: top(hover), height: Math.max(newDur * PXM, 16) }}>
            <div className="h-full rounded bg-brand-100/60 ring-2 ring-brand-400 flex items-start justify-center pt-0.5 text-[11px] font-semibold text-brand-700 tabular-nums">
              + {hm(hover)}–{hm(hover + newDur)}
            </div>
            <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1 whitespace-nowrap rounded-md bg-slate-800/90 px-1.5 py-0.5 text-[11px] font-medium text-white shadow">
              ทำนัด {col.name} {hm(hover)}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
